import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AxiosError } from 'axios';

import { PageLoader } from 'components/PageLoader';
import { Form, FormTextArea } from 'components/form';
import { messageSchema } from 'utils/validators';

import { getInquiry, replyToInquiry } from './api';
import * as styled from './styles';

type InquiryData = {
  sender_name: string;
  message: string;
  replied_at: string | null;
};

const replySchema = z.object({ reply: messageSchema });
type ReplyFormValues = z.infer<typeof replySchema>;

export default function InquiryReply() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<InquiryData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const form = useForm<ReplyFormValues>({
    resolver: zodResolver(replySchema),
    mode: 'onBlur',
    defaultValues: { reply: '' },
  });

  useEffect(() => {
    if (!token) return;
    getInquiry(token)
      .then((response) => setData(response.data.inquiry))
      .catch(() => setLoadError('This link is invalid or has expired.'));
  }, [token]);

  const handleSend = (values: ReplyFormValues) => {
    if (!token) return;
    setSubmitError(null);
    return replyToInquiry(token, values.reply.trim())
      .then(() => setSent(true))
      .catch((error: AxiosError<{ error?: string }>) => {
        const body = error.response?.data?.error;
        if (body?.includes('already been answered')) setSubmitError(body);
        else setSubmitError('Could not send your reply. Please try again.');
      });
  };

  if (loadError) return <styled.LoadError>{loadError}</styled.LoadError>;
  if (!data) return <PageLoader fullPage />;

  const alreadyReplied = !!data.replied_at;

  return (
    <div>
      <styled.TopBar>
        <styled.Logo>Kudable</styled.Logo>
      </styled.TopBar>

      <styled.Container>
        <styled.Card>
          {sent || alreadyReplied ? (
            <styled.SuccessBox>
              <styled.SuccessIcon>✓</styled.SuccessIcon>
              <styled.SuccessTitle>
                {sent ? 'Reply sent!' : 'Already replied'}
              </styled.SuccessTitle>
              <styled.SuccessBody>
                {sent
                  ? `${data.sender_name} will receive your response by email.`
                  : "You've already answered this question."}
              </styled.SuccessBody>
            </styled.SuccessBox>
          ) : (
            <Form form={form} onSubmit={handleSend}>
              <styled.Title>Reply to a question</styled.Title>
              <styled.Subtitle>
                {data.sender_name} asked about your experience:
              </styled.Subtitle>
              <styled.QuestionBox>"{data.message}"</styled.QuestionBox>

              <FormTextArea
                name="reply"
                placeholder="Type your reply..."
                rows={4}
              />

              {submitError && (
                <styled.ErrorText>{submitError}</styled.ErrorText>
              )}

              <styled.SendBtn
                type="submit"
                disabled={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting ? 'Sending...' : 'Send reply'}
              </styled.SendBtn>
            </Form>
          )}
        </styled.Card>
      </styled.Container>
    </div>
  );
}
